<template>
  <div id="servicos-modal-app">
    <modal
      elem-id="modal_crud_servicos"
      size="small"
      :close-label="t('Fechar')"
      :save-label="t('Salvar')"
      :save-disabled-fn="() => !formRef?.canSave"
      :save-fn="handleSave"
      :title="modalTitle"
      :show="isVisible"
      @close="closeModal"
      @dismiss="closeModal"
      >

      <!-- Content only renders when modal is visible (prevents API calls on mount) -->
      <template v-if="isVisible">
        <!-- Action Buttons -->
        <div class="text-right" style="margin-bottom: 20px;">
          <Button
            v-if="shouldShowDelete"
            data-testid="servicos-btn-delete"
            severity="danger"
            :label="t('Excluir')"
            icon="pi pi-trash"
            @click="handleDelete"
          />
        </div>

        <!-- Form -->
        <servicos-form
          ref="formRef"
          data-testid="servicos-form-wrapper"
          :servico-id="currentServicoId"
          @saved="handleSaveSuccess"
          @cancel="closeModal">
        </servicos-form>
      </template>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useServicos } from '../../composables/useServicos';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { log } from '../../utils/DebugLogger';
import Modal from '../../ui/Modal.vue';
import ServicosForm from './ServicosForm.vue';

interface ServicosFormExposed {
  initializeForNew: () => Promise<void>;
  loadForEdit: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  canSave: boolean;
}

interface ModalOpenPayload {
  id?: number | string | null;
}

const { t } = useI18n();
const { confirmAction } = useConfirmAction();
const { deleteServico } = useServicos();

const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const formRef = ref<ServicosFormExposed | null>(null);
const currentServicoId = ref<number | string | null>(null);

const modalTitle = computed<string>(() => {
  return isEditMode.value ? t('Editar Serviço') : t('Novo Serviço');
});

const shouldShowDelete = computed<boolean>(() => {
  return isEditMode.value && currentServicoId.value !== null && currentServicoId.value !== undefined;
});

const openForNew = async (): Promise<void> => {
  log('ServicosModal', 'openForNew', 'Opening modal for new servico');
  isVisible.value = true;
  isEditMode.value = false;
  currentServicoId.value = null;

  await nextTick();

  if (formRef.value) {
    await formRef.value.initializeForNew();
  }
};

const openForEdit = async (servicoId: number | string): Promise<void> => {
  log('ServicosModal', 'openForEdit', 'Opening modal for edit', servicoId);
  isVisible.value = true;
  isEditMode.value = true;
  currentServicoId.value = servicoId;
};

const closeModal = (): void => {
  log('ServicosModal', 'closeModal', 'Closing modal');
  isVisible.value = false;
  isEditMode.value = false;
  currentServicoId.value = null;
};

const handleSave = async (): Promise<void> => {
  log('ServicosModal', 'handleSave', 'Saving servico');
  if (formRef.value) {
    await formRef.value.handleSubmit();
  }
};

const handleSaveSuccess = (): void => {
  log('ServicosModal', 'handleSaveSuccess', 'Servico saved successfully');
  closeModal();
  eventBus.emit('servicos.refresh');
};

const handleDelete = (): void => {
  if (!currentServicoId.value) return;

  confirmAction({
    message: t('Tem certeza que deseja excluir este serviço?'),
    header: t('Confirmar Exclusão'),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('Sim'),
    rejectLabel: t('Não'),
    onAccept: async () => {
      log('ServicosModal', 'handleDelete', 'Deleting servico', currentServicoId.value);

      try {
        await deleteServico(Number(currentServicoId.value));
        log('ServicosModal', 'handleDelete', 'Servico deleted successfully');

        eventBus.emit('globalMessage.success', t('Serviço excluído com sucesso'));
        closeModal();
        eventBus.emit('servicos.refresh');
      } catch (error) {
        log('ServicosModal', 'handleDelete', 'Error deleting servico', error);
        eventBus.emit('globalMessage.error', t('Erro ao excluir serviço'));
      }
    }
  });
};

const handleModalOpen = (data?: ModalOpenPayload): void => {
  log('ServicosModal', 'handleModalOpen', 'Received open event', data);
  if (data?.id) {
    void openForEdit(data.id);
  } else {
    void openForNew();
  }
};

const handleModalClose = (): void => {
  log('ServicosModal', 'handleModalClose', 'Received close event');
  closeModal();
};

onMounted(() => {
  log('ServicosModal', 'mounted', 'Component mounted, registering eventBus listeners');
  eventBus.on('modal.servicos.open', handleModalOpen);
  eventBus.on('modal.servicos.close', handleModalClose);
});

onUnmounted(() => {
  log('ServicosModal', 'unmounted', 'Component unmounted, removing eventBus listeners');
  eventBus.off('modal.servicos.open', handleModalOpen);
  eventBus.off('modal.servicos.close', handleModalClose);
});
</script>

<style scoped>
/* Modal styles are handled by Modal component */
</style>
